import { useMemo, useState, useCallback, useRef } from 'react';
import {
  PatientSearchFilters,
  SearchFilterType,
  type GetSearchPatientFiltersPayload,
  type QuickFilter,
} from '../types';
import { post } from '../../../../api/baseUrl';
import {
  currentSearchFilterInputNeeded,
  currentSearchFilterMultipleSelection,
  currentSearchFilterRemoteOptionsNeeded,
  generateExistingData,
  generateFilterOption,
  getQuickFilterKey,
  getRemainingValidOperators,
  isDoNothingSelect,
} from '../utils';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../store';
import {
  setCurrentFilter as setGlobalCurrentFilter,
  setFromNavBar,
} from '../../../../store/ai2';
import { useNavigate } from 'react-router-dom';
import { useFilteredOptions } from './useFiltersOption';
import {
  mapRelationaOperatorToText,
  mapTextToRelationaOperator,
} from '../validator/main';

const SEARCH_FILTERS_API = 'getSearchFilters';

export enum SelectionStatus {
  MULTIPLE = 'multiple',
  SINGLE = 'single',
  AGE = 'age',
  SPECIFIC_PATIENT = 'specific_patient',
}
export const useFilterSelection = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { clinics, selectedTenancy, teams } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );
  const searchFiltersMap = selectedTenancy?.features?.searchFiltersMap || {};

  const allOptions = useFilteredOptions();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<QuickFilter[]>([]);
  const [currentFilter, setCurrentFilter] = useState<QuickFilter | null>(null);
  const [fetchedFilters, setFetchedFilters] = useState<string[]>([]);
  const [flowStatus, setFlowStatus] = useState<SelectionStatus>(
    SelectionStatus.SINGLE,
  );
  const abortControllerRef = useRef<AbortController | null>(null);
  const isFetchingRef = useRef(false);

  const endFlow = useCallback(() => {
    setCurrentFilter(() => null);
    setFlowStatus(() => SelectionStatus.SINGLE);
    setFetchedFilters([]);
  }, [setCurrentFilter, setFlowStatus]);

  const fetchRemoteFilters = useCallback(
    async (filter: QuickFilter, searchText?: string) => {
      if (!selectedTenancy?.tenancy_id) {
        return;
      }

      const existingData = generateExistingData(clinics, teams);
      const quickFilterKey = getQuickFilterKey(filter, searchFiltersMap);
      const existingDataLabel =
        quickFilterKey && quickFilterKey in PatientSearchFilters
          ? PatientSearchFilters[
              quickFilterKey as keyof typeof PatientSearchFilters
            ]
          : undefined;
      if (currentSearchFilterInputNeeded(filter.type) && !searchText) {
        return;
      }
      if (
        existingData &&
        existingDataLabel &&
        existingData[existingDataLabel]?.length > 0
      ) {
        const regex = new RegExp(searchText || '', 'i');
        const filteredExistingDataByText = existingData[
          existingDataLabel
        ].filter((data) => regex.test(data));
        setFetchedFilters(filteredExistingDataByText);
        return;
      }
      abortControllerRef.current?.abort();

      const newController = new AbortController();
      abortControllerRef.current = newController;
      try {
        setIsFetching(true);
        const requestBody: GetSearchPatientFiltersPayload = {
          filter: filter.label,
          tenancyId: selectedTenancy.tenancy_id,
          searchText,
        };
        const {
          data: { data },
        } = await post(
          SEARCH_FILTERS_API,
          requestBody,
          undefined,
          undefined,
          newController.signal,
        );
        setFetchedFilters(data || []);
      } catch (error) {
        if ((error as any)?.name === 'AbortError') {
          setIsFetching(false);
          return;
        }
      } finally {
        if (abortControllerRef.current?.signal === newController.signal) {
          isFetchingRef.current = false;
          setIsFetching(false);
        }
      }
    },
    [setIsFetching, setFetchedFilters],
  );
  const setTextValue = (value: string) => {
    const textFilter: QuickFilter = {
      id: `${PatientSearchFilters.AgeFilter}-age-${value}-${selectedFilters.length}`,
      label: value,
      type: SearchFilterType.NO_SELECTION,
      filterKey: 'AgeFilter',
    };
    setSelectedFilters((prevFilters) => [...prevFilters, textFilter]);
  };

  const handleFilterSelection = useCallback(
    async (filter: QuickFilter, searchText?: string) => {
      let searchPatientFilter: QuickFilter | null = null;
      if (
        selectedFilters[selectedFilters.length - 1] &&
        selectedFilters[selectedFilters.length - 1].filterKey ===
          'SpecificPatient' &&
        searchText
      ) {
        searchPatientFilter = generateFilterOption(
          searchText,
          `${PatientSearchFilters.SpecificPatient.trim()}-${selectedFilters.length}`,
          {
            type: SearchFilterType.NO_SELECTION,
          },
        );
      }
      if (filter.id === 'all-patient-filter') {
        dispatch(setFromNavBar(false));
        dispatch(setGlobalCurrentFilter(['All Patients']));
        navigate('/dashboard');
        return;
      }
      const shouldEndSingleFlow =
        flowStatus !== SelectionStatus.MULTIPLE &&
        flowStatus !== SelectionStatus.AGE &&
        filter.type === SearchFilterType.NO_SELECTION;
      // Clean up remote options if not multi-selection
      if (
        flowStatus !== SelectionStatus.MULTIPLE &&
        flowStatus !== SelectionStatus.AGE
      ) {
        setFetchedFilters([]);
      }
      if (filter.filterKey === 'AgeFilter') {
        setFlowStatus(SelectionStatus.AGE);
      } else if (filter.filterKey === 'SpecificPatient') {
        setFlowStatus(SelectionStatus.SPECIFIC_PATIENT);
      } else if (currentSearchFilterMultipleSelection(filter.type)) {
        setFlowStatus(SelectionStatus.MULTIPLE);
      }

      const filters = searchPatientFilter
        ? [...selectedFilters, searchPatientFilter, filter]
        : [...selectedFilters, filter];

      setSelectedFilters(filters);
      if (shouldEndSingleFlow) {
        endFlow();
        return;
      }
      setCurrentFilter(filter);

      if (
        currentSearchFilterRemoteOptionsNeeded(filter.type) &&
        filter.filterKey !== 'SpecificPatient' &&
        !isDoNothingSelect(filter.filterKey as PatientSearchFilters)
      ) {
        await fetchRemoteFilters(filter);
      }
    },
    [
      selectedFilters,
      setCurrentFilter,
      setSelectedFilters,
      fetchRemoteFilters,
      flowStatus,
    ],
  );

  const continueNext = (searchText: string) => {
    // 1. Add current search text as a filter and reset the search text
    setTextValue(searchText);
    // 2. continue selecting age filters
    const ageFilter = selectedFilters.find(
      (filter) => filter.filterKey === 'AgeFilter',
    );
    if (ageFilter) {
      setCurrentFilter(ageFilter);
    }
  };

  const onRemoveSelectedFilter = (filter: QuickFilter) => {
    const selected = [...selectedFilters];
    let restFilters = selected.filter(
      (prevFilter) =>
        !prevFilter.id.includes(filter.id) &&
        !filter.id.includes(prevFilter.id),
    );
    if (
      filter.filterKey === 'AgeFilter' ||
      (flowStatus !== SelectionStatus.AGE &&
        filter.id.includes(PatientSearchFilters.AgeFilter))
    ) {
      restFilters = restFilters.filter(
        (prevFilter) =>
          prevFilter.filterKey !== 'AgeFilter' &&
          !prevFilter.id.includes(PatientSearchFilters.AgeFilter),
      );
    }
    setSelectedFilters(restFilters);
    const previousSelectedFilter =
      selectedFilters.length > 0 ? restFilters[restFilters.length - 1] : null;
    setCurrentFilter(previousSelectedFilter || null);
    if (
      filter.filterKey === 'AgeFilter' ||
      flowStatus !== SelectionStatus.AGE
    ) {
      endFlow();
    }
  };

  const availableFilterOptions: QuickFilter[] = useMemo(() => {
    if (flowStatus === SelectionStatus.AGE) {
      if (currentFilter?.type === SearchFilterType.NO_SELECTION) {
        return [];
      }
      const existingData = generateExistingData(clinics, teams);
      const ageOptions = existingData
        ? existingData[PatientSearchFilters.AgeFilter].map((filter, index) =>
            generateFilterOption(
              mapRelationaOperatorToText(filter),
              `${PatientSearchFilters.AgeFilter}-${filter}-${index}`,
            ),
          )
        : [];
      const allSelectedAgeFilters = selectedFilters
        .filter(
          (f) =>
            f.filterKey !== 'AgeFilter' &&
            f.id.includes(PatientSearchFilters.AgeFilter),
        )
        .map((f) => f.label);
      const availableOps = getRemainingValidOperators(
        ageOptions,
        allSelectedAgeFilters.map(mapTextToRelationaOperator),
      );
      if (availableOps.length === 0) {
        endFlow();
      }
      return availableOps;
    }
    const selectedIds = selectedFilters.map((filter) => filter.id);
    const selectedFilterLabels = selectedFilters.map(
      (filter) => `${filter.filterKey || ''}:${filter.label}`,
    );
    const remoteParentId = currentFilter?.id || 'remote-filter';
    const remoteFilters: QuickFilter[] = fetchedFilters.map((label) => ({
      id: `${remoteParentId}-${label}`,
      label,
      type: SearchFilterType.NO_SELECTION,
      filterKey: undefined,
    }));
    const allAvailableOptions =
      flowStatus === SelectionStatus.MULTIPLE
        ? remoteFilters.filter(
            (option) =>
              !selectedFilterLabels.includes(
                `${option.filterKey || ''}:${option.label}`,
              ),
          )
        : allOptions.filter((option) => !selectedIds.includes(option.id));

    if (
      currentFilter?.type &&
      (currentSearchFilterRemoteOptionsNeeded(currentFilter.type) ||
        currentSearchFilterInputNeeded(currentFilter.type))
    ) {
      return remoteFilters;
    }
    if (
      allAvailableOptions.length === 0 &&
      flowStatus !== SelectionStatus.MULTIPLE
    ) {
      endFlow();
    }
    return allAvailableOptions;
  }, [selectedFilters, currentFilter, fetchedFilters, flowStatus]);

  return {
    selectedFilters,
    isFetching,
    availableFilterOptions,
    handleFilterSelection,
    onRemoveSelectedFilter,
    inputRef,
    endFlow,
    currentFilter,
    currentFlow: currentFilter?.type || null,
    fetchRemoteFilters,
    flowStatus,
    continueNext,
  };
};
